import React, { useRef, useState } from "react";
import {
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  Input,
  Text,
  Box,
  VStack,
  Icon,
  Tag,
  TagLabel,
  TagCloseButton,
  useToast
} from "@chakra-ui/react";
import { DownloadIcon } from "@chakra-ui/icons";
import { FaCloudUploadAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import { getNeedsImportTemplate, importNeedsFromFile } from "../../../api/needsApi";
import { FieldLabel } from "../../../components/Portal/ui";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX,
} from "../../../components/Portal/portalModalChrome";

const ImportNeedsFromFileModal = ({ isOpen, onClose, onSuccess }) => {
  const { selectedBusinessUnitId } = useBusinessUnit();
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const fileInputRef = useRef(null);
  const toast = useToast();
  const { t, i18n } = useTranslation("portal");

  const buildImportSummary = (payload) => {
    const total = payload?.totalRows;
    const added = payload?.addedRows;
    const updated = payload?.updatedRows;
    const deleted = payload?.deletedRows;
    const duplicate = payload?.duplicateRows;
    const parts = [];
    if (total != null) parts.push(`${t("pilotNeeds.importFromFile.statusLabels.total")}: ${total}`);
    if (added != null) parts.push(`${t("pilotNeeds.importFromFile.statusLabels.added")}: ${added}`);
    if (updated != null) parts.push(`${t("pilotNeeds.importFromFile.statusLabels.updated")}: ${updated}`);
    if (deleted != null) parts.push(`${t("pilotNeeds.importFromFile.statusLabels.deleted")}: ${deleted}`);
    if (duplicate != null) parts.push(`${t("pilotNeeds.importFromFile.statusLabels.duplicate")}: ${duplicate}`);
    return parts.length ? parts.join(" · ") : "";
  };

  const isSupportedFile = (f) => {
    if (!f) return false;
    const mime = f.type;
    const name = (f.name || "").toLowerCase();

    if (
      mime === "text/csv" ||
      mime === "application/vnd.ms-excel" ||
      mime === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      return true;
    }

    if (name.endsWith(".csv") || name.endsWith(".xlsx")) {
      return true;
    }

    return false;
  };

  const handleFilesSelected = (event) => {
    const selected = event.target.files?.[0] || null;
    if (selected && isSupportedFile(selected)) {
      setFile(selected);
    } else if (selected) {
      toast({
        title: t("pilotNeeds.importFromFile.toasts.invalidFileTypeTitle"),
        description: t("pilotNeeds.importFromFile.toasts.invalidFileTypeDescription"),
        status: "error"
      });
    }
    event.target.value = null;
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const f = e.dataTransfer.files[0];
      if (isSupportedFile(f)) {
        setFile(f);
      } else {
        toast({
          title: t("pilotNeeds.importFromFile.toasts.invalidFileTypeTitle"),
          description: t("pilotNeeds.importFromFile.toasts.invalidFileTypeDescription"),
          status: "error"
        });
      }
    }
  };

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const response = await getNeedsImportTemplate({
        buId: selectedBusinessUnitId || undefined,
        lang: i18n.resolvedLanguage || i18n.language
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "asap_pilot_needs_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast({
        title: t("pilot.import.templateError", { defaultValue: "Failed to download template." }),
        status: "error"
      });
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleSubmit = async () => {
    if (!file) return;
    setIsSubmitting(true);
    try {
      const res = await importNeedsFromFile(file, {
        buId: selectedBusinessUnitId || undefined
      });
      const summary = buildImportSummary(res);
      toast({
        title: t("pilotNeeds.importFromFile.toasts.importCompleted"),
        description: summary || undefined,
        status: "success"
      });
      onSuccess?.(res);
      onClose?.();
      setFile(null);
    } catch (e) {
      toast({
        title: t("pilotNeeds.importFromFile.toasts.importFailedTitle"),
        description:
          e?.response?.data?.message ||
          t("pilotNeeds.importFromFile.toasts.importFailedDescription"),
        status: "error"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay bg="rgba(11,31,58,.45)" />
      <ModalContent sx={MODAL_CONTENT_SX}>
        <ModalHeader sx={MODAL_HEADER_SX}>{t("pilotNeeds.importFromFile.modalTitle")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody py="16px">
          <Text fontSize="13px" color="portal.muted" mb="14px" sx={{ marginBottom: "14px !important" }}>
            {t("pilotNeeds.importFromFile.description")}
          </Text>
          <HStack mb="16px">
            <Button
              leftIcon={<DownloadIcon />}
              size="sm"
              variant="secondary"
              onClick={handleDownloadTemplate}
              isLoading={downloadingTemplate}
              data-testid="download-needs-template"
            >
              {t("pilot.import.downloadTemplate", { defaultValue: "Download template" })}
            </Button>
          </HStack>
          <FormControl isRequired>
            <FieldLabel>{t("pilotNeeds.importFromFile.fields.file")}</FieldLabel>
            {file ? (
              <Tag size="md" variant="subtle" colorScheme="brand">
                <TagLabel>{file.name}</TagLabel>
                <TagCloseButton onClick={() => setFile(null)} />
              </Tag>
            ) : (
              <Box
                border="2px dashed"
                borderColor={dragActive ? "portal.primary" : "portal.line"}
                borderRadius="10px"
                p={6}
                textAlign="center"
                minH="160px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg={dragActive ? "portal.active" : "portal.soft"}
                transition="all 0.2s"
                cursor="pointer"
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                _hover={{ bg: "portal.active", borderColor: "portal.primary" }}
              >
                <VStack spacing={2}>
                  <Icon as={FaCloudUploadAlt} boxSize={8} color="portal.primary" />
                  <Text fontSize="13px" fontWeight="700" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                    {t("pilotNeeds.importFromFile.dropzone.text")}
                  </Text>
                  <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                    {t("pilotNeeds.importFromFile.dropzone.accepted")}
                  </Text>
                </VStack>
                <Input
                  ref={fileInputRef}
                  type="file"
                  aria-required="true"
                  accept=".csv,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={handleFilesSelected}
                  display="none"
                />
              </Box>
            )}
          </FormControl>
        </ModalBody>
        <ModalFooter sx={MODAL_FOOTER_SX}>
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t("pilotNeeds.importFromFile.buttons.cancel")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            isDisabled={!file}
            isLoading={isSubmitting}
          >
            {t("pilotNeeds.importFromFile.buttons.import")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ImportNeedsFromFileModal;
